import { api } from '../components/role-play/apiService';
import { getIdToken } from '../utils/keycloak';
import { getBackendUrl } from '../utils/utils';
import i18n from 'i18next';
import type { AssignmentRules, PracticeType } from './assignmentService';

/**
 * Student system: classes, rosters, consent, and the org's student-system settings.
 *
 * School admins manage classes and rosters (`/classes/*`); teachers and students reach
 * their own classes through `/me/classes`. Teacher and student are class memberships,
 * not app roles, so a user's `role` alone never says whether they teach or study.
 */

export type ClassRole = 'teacher' | 'co_teacher' | 'student';
export type ClassStatus = 'active' | 'archived';
export type ConsentType = 'student' | 'guardian' | 'school';

export interface SchoolClass {
  id: string;
  organization_id: string;
  name: string;
  subject?: string | null;
  grade_range?: string | null;
  school_year?: string | null;
  status: ClassStatus;
  archived_at?: string | null;
  /** Set once the class was rolled over into next year's class. */
  rolled_over_to_id?: string | null;
  created_at: string;
  updated_at?: string | null;
  teacher_count: number;
  student_count: number;
  /** The caller's own membership role, when they have one. */
  my_role?: ClassRole | null;
}

export interface ClassInput {
  name?: string;
  subject?: string | null;
  grade_range?: string | null;
  school_year?: string | null;
  status?: ClassStatus;
  /** Super admin only, on create. */
  organization_id?: string;
}

export interface ClassList {
  items: SchoolClass[];
  pagination: { page: number; page_size: number; total_count: number; total_pages: number };
}

export interface ClassMember {
  user_id: string;
  role: ClassRole;
  display_name?: string | null;
  email?: string | null;
  starts_at: string;
  ends_at?: string | null;
  /** Students only, and only while the school requires consent. */
  has_consent?: boolean | null;
}

export interface Roster {
  class_id: string;
  members: ClassMember[];
  consent_required: boolean;
  consent_type: ConsentType;
}

export type ImportOutcome = 'added' | 'reactivated' | 'updated' | 'unchanged' | 'skipped';

export interface RosterImportResult {
  added: number;
  reactivated: number;
  updated: number;
  unchanged: number;
  skipped: number;
  rows: Array<{ row: number; email: string; outcome: ImportOutcome; reason?: string | null }>;
}

export interface MyClass {
  id: string;
  organization_id: string;
  name: string;
  subject?: string | null;
  grade_range?: string | null;
  school_year?: string | null;
  /** `shared`: the class was shared with the caller (an access grant), not joined. */
  role: ClassRole | 'shared';
  grant_scope?: GrantScope | null;
  grant_expires_at?: string | null;
}

export interface MyConsent {
  required: boolean;
  consent_type?: ConsentType | null;
  granted: boolean;
  /** The school asks students to accept for themselves, so the consent screen can. */
  can_self_consent: boolean;
  organization_id?: string | null;
}

export interface MyClasses {
  classes: MyClass[];
  consent: MyConsent;
}

export interface StudentSystemSettings {
  organization_id: string;
  student_system_enabled: boolean;
  student_consent_required: boolean;
  student_consent_type: ConsentType;
  /** What a teacher's Assign Practice form starts from. No session length: content
   *  runs for its own length. */
  student_default_max_attempts: number;
  student_default_session_minutes: number | null;
  /** Students type and read: no microphone, no AI voice. */
  student_text_only: boolean;
  /** Off: the microphone is never opened and students type. Audio is never stored. */
  student_audio_recording_enabled: boolean;
  /** Off: a student sees the AI's feedback on a class assignment only once their
   *  teacher releases the grade. */
  student_ai_feedback_before_release: boolean;
  /** On: teachers can assign only content a school admin approved. */
  student_content_approval_required: boolean;
  /** Days an archived class's student data is kept before the retention job deletes it. */
  student_data_retention_days: number;
  /** Set for a while after the period changes: nothing is deleted before then. */
  student_data_retention_grace_until?: string | null;
}

/** Bounds on the retention period, as the server checks them. */
export const RETENTION_DAYS_MIN = 30;
export const RETENTION_DAYS_MAX = 3650;

export type StudentSystemSettingsInput = Partial<
  Omit<StudentSystemSettings, 'organization_id' | 'student_data_retention_grace_until'>
>;

const orgQuery = (organizationId?: string | null) =>
  organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : '';

// ── Settings ───────────────────────────────────────────────────────────────

export function fetchStudentSystemSettings(organizationId?: string | null): Promise<StudentSystemSettings> {
  return api.get<StudentSystemSettings>(`/student-system/settings${orgQuery(organizationId)}`);
}

export function updateStudentSystemSettings(
  input: StudentSystemSettingsInput,
  organizationId?: string | null
): Promise<StudentSystemSettings> {
  return api.put<StudentSystemSettings>(`/student-system/settings${orgQuery(organizationId)}`, input);
}

// ── Classes ────────────────────────────────────────────────────────────────

export function fetchClasses(params: {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: ClassStatus | '';
  organizationId?: string | null;
}): Promise<ClassList> {
  const query = new URLSearchParams({ page: String(params.page ?? 1), page_size: String(params.pageSize ?? 25) });
  if (params.search) query.set('search', params.search);
  if (params.status) query.set('status', params.status);
  if (params.organizationId) query.set('organization_id', params.organizationId);
  return api.get<ClassList>(`/classes?${query.toString()}`);
}

export function createClass(input: ClassInput): Promise<SchoolClass> {
  return api.post<SchoolClass>('/classes', input);
}

export function fetchClass(classId: string): Promise<SchoolClass> {
  return api.get<SchoolClass>(`/classes/${classId}`);
}

export function updateClass(classId: string, input: ClassInput): Promise<SchoolClass> {
  return api.patch<SchoolClass>(`/classes/${classId}`, input);
}

// ── Roster ─────────────────────────────────────────────────────────────────

export function fetchRoster(classId: string): Promise<Roster> {
  return api.get<Roster>(`/classes/${classId}/members`);
}

export function addClassMember(classId: string, email: string, role: ClassRole): Promise<ClassMember> {
  return api.post<ClassMember>(`/classes/${classId}/members`, { email, role });
}

export function removeClassMember(classId: string, userId: string): Promise<void> {
  return api.delete<void>(`/classes/${classId}/members/${encodeURIComponent(userId)}`);
}

/** Multipart, so it goes through fetch directly: `api` only sends JSON. */
export async function importClassRoster(classId: string, file: File): Promise<RosterImportResult> {
  const token = await getIdToken();
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(getBackendUrl(`/classes/${classId}/members/import`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data.detail || i18n.t('common.apiError', { status: response.status }));
    (error as any).status = response.status;
    throw error;
  }
  return (await response.json()) as RosterImportResult;
}

export function recordStudentConsent(classId: string, userId: string): Promise<ClassMember> {
  return api.post<ClassMember>(`/classes/${classId}/members/${encodeURIComponent(userId)}/consent`, {});
}

export function revokeStudentConsent(classId: string, userId: string): Promise<ClassMember> {
  return api.delete<ClassMember>(`/classes/${classId}/members/${encodeURIComponent(userId)}/consent`);
}

// ── The signed-in user's own classes ───────────────────────────────────────

export function fetchMyClasses(): Promise<MyClasses> {
  return api.get<MyClasses>('/me/classes');
}

export function acceptMyConsent(): Promise<MyConsent> {
  return api.post<MyConsent>('/me/consent', {});
}

// ── Class assignments (Assign Practice) ────────────────────────────────────

export interface AssignableItem {
  item_type: 'scenario' | 'prompt';
  name: string;
  difficulty_mode?: string | null;
}

export type Difficulty = 'easy' | 'medium' | 'hard';

/** A school admin's tags on a bundle, so teachers can find it. */
export interface ContentTags {
  skill?: string | null;
  subject?: string | null;
  grade?: string | null;
  language?: string | null;
  difficulty?: Difficulty | null;
}

export interface AssignableBundle {
  id: string;
  name: string;
  description?: string | null;
  items: AssignableItem[];
  tags?: ContentTags;
}

export interface AssignableContent {
  bundles: AssignableBundle[];
  /** The school's defaults, which the Assign Practice form starts from. */
  default_max_attempts: number;
  default_session_minutes: number | null;
}

export interface ClassAssignmentDraft {
  title: string;
  description?: string | null;
  bundle_ids: string[];
  /** ISO timestamp. */
  due_date?: string | null;
  /** Left out: the school's default. */
  max_attempts?: number | null;
  unlimited_attempts?: boolean;
  session_minutes?: number | null;
  hints_allowed: boolean;
  objectives: string[];
  rubric?: string | null;
  /** The top of the rubric's scale; left out, a 4-point rubric. */
  rubric_max_score?: number | null;
  accommodations?: string | null;
  practice_type?: PracticeType | null;
}

export interface ClassAssignmentProgress {
  students: number;
  not_started: number;
  in_progress: number;
  completed: number;
}

export interface ClassAssignment {
  id: string;
  title: string;
  description?: string | null;
  due_date?: string | null;
  assignment_status: 'draft' | 'published' | 'expired' | 'archived';
  created_at: string;
  bundle_names: string[];
  rules: AssignmentRules;
  progress: ClassAssignmentProgress;
}

export interface AssignmentPreview {
  title: string;
  description?: string | null;
  due_date?: string | null;
  class_name: string;
  rules: AssignmentRules;
  bundles: AssignableBundle[];
  recipients: number;
}

export function fetchAssignableContent(classId: string): Promise<AssignableContent> {
  return api.get<AssignableContent>(`/classes/${classId}/assignable-content`);
}

export function fetchClassAssignments(classId: string): Promise<ClassAssignment[]> {
  return api.get<ClassAssignment[]>(`/classes/${classId}/assignments`);
}

export function previewClassAssignment(classId: string, draft: ClassAssignmentDraft): Promise<AssignmentPreview> {
  return api.post<AssignmentPreview>(`/classes/${classId}/assignments/preview`, draft);
}

/** `publish: false` saves a draft. */
export function createClassAssignment(
  classId: string,
  draft: ClassAssignmentDraft,
  publish: boolean
): Promise<ClassAssignment> {
  return api.post<ClassAssignment>(`/classes/${classId}/assignments`, { ...draft, publish });
}

export function publishClassAssignment(classId: string, assignmentId: string): Promise<ClassAssignment> {
  return api.post<ClassAssignment>(`/classes/${classId}/assignments/${assignmentId}/publish`, {});
}

export function archiveClassAssignment(classId: string, assignmentId: string): Promise<void> {
  return api.post<void>(`/classes/${classId}/assignments/${assignmentId}/archive`, {});
}

// ── Student practice: reflection and reports ─────────────────────────────────

export interface ReflectionAnswers {
  went_well?: string | null;
  try_next?: string | null;
  /** 1 (not at all) to 5 (very). */
  confidence?: number | null;
}

export interface SessionReflection {
  roleplay_session_id: string;
  answers: ReflectionAnswers;
  created_at: string;
  updated_at?: string | null;
}

/** What a student can report. The platform files its own `ai_output` and `safety_concern`. */
export type StudentReportType = 'inappropriate' | 'upsetting' | 'incorrect' | 'other';
export type ReportStatus = 'open' | 'resolved' | 'dismissed';

export interface ContentReport {
  id: string;
  roleplay_session_id: string;
  class_id?: string | null;
  reported_by: string;
  reporter_name?: string | null;
  report_type: StudentReportType | 'ai_output' | 'safety_concern';
  details?: string | null;
  message_text?: string | null;
  status: ReportStatus;
  created_at: string;
}

export function fetchReflection(sessionId: string): Promise<SessionReflection | null> {
  return api.get<SessionReflection | null>(`/practice/sessions/${sessionId}/reflection`);
}

export function saveReflection(sessionId: string, answers: ReflectionAnswers): Promise<SessionReflection> {
  return api.put<SessionReflection>(`/practice/sessions/${sessionId}/reflection`, answers);
}

export function reportContent(
  sessionId: string,
  report: { report_type: StudentReportType; details?: string | null; message_text?: string | null }
): Promise<ContentReport> {
  return api.post<ContentReport>(`/practice/sessions/${sessionId}/reports`, report);
}

export function fetchClassReports(classId: string, status?: ReportStatus): Promise<ContentReport[]> {
  return api.get<ContentReport[]>(`/classes/${classId}/reports${status ? `?status=${status}` : ''}`);
}

export function updateClassReport(classId: string, reportId: string, status: ReportStatus): Promise<ContentReport> {
  return api.patch<ContentReport>(`/classes/${classId}/reports/${reportId}`, { status });
}

// ── Review and grading ─────────────────────────────────────────────────────

/** One attempt at a class assignment, as the teacher's review list shows it. */
export interface AttemptSummary {
  id: string;
  roleplay_session_id?: string | null;
  assignment_id: string;
  assignment_title: string;
  content_name?: string | null;
  student_id: string;
  student_name?: string | null;
  attempt_no: number;
  started_at: string;
  session_status?: string | null;
  feedback_status?: string | null;
  /** The top of the rubric's scale; scores run from 0 to it. */
  max_score: number;
  /** The AI's, on the rubric's scale. The student never sees it. */
  suggested_score?: number | null;
  /** The teacher's score before release. */
  draft_score?: number | null;
  /** The released grade: set only by release. */
  final_score?: number | null;
  released_at?: string | null;
}

/** A page of a list, and how many items match in all. */
export interface Page<T> {
  items: T[];
  total: number;
}

/** The review page's attempt. Its transcript and AI feedback come from the session. */
export interface AttemptReview extends AttemptSummary {
  suggestion_rationale?: string | null;
  review_comment?: string | null;
  reviewed_by?: string | null;
  rubric?: string | null;
  objectives: string[];
  reflection?: ReflectionAnswers | null;
  /** The student's attempts at the same assignment, oldest first. */
  history: AttemptSummary[];
  /** Teachers, school admins and grade-scope grantees; a view grant only reads. */
  can_grade: boolean;
}

export type AttemptListStatus = 'to_review' | 'released' | 'all';

/** A released grade, as the student sees it. */
export interface MyGrade {
  attempt_id: string;
  roleplay_session_id?: string | null;
  assignment_id: string;
  assignment_title: string;
  class_name?: string | null;
  content_name?: string | null;
  attempt_no: number;
  final_score: number;
  max_score: number;
  comment?: string | null;
  released_at: string;
}

/** The server's page size for the review queue and My Grades. */
export const GRADING_PAGE_SIZE = 100;

export function fetchClassAttempts(
  classId: string,
  status: AttemptListStatus = 'to_review',
  offset = 0
): Promise<Page<AttemptSummary>> {
  return api.get<Page<AttemptSummary>>(
    `/classes/${classId}/attempts?status=${status}&limit=${GRADING_PAGE_SIZE}&offset=${offset}`
  );
}

export function fetchAttemptReview(classId: string, attemptId: string): Promise<AttemptReview> {
  return api.get<AttemptReview>(`/classes/${classId}/attempts/${attemptId}`);
}

/** Save a draft: the student sees nothing until the grade is released. Only the fields
 *  sent change; `null` clears one. */
export function saveAttemptReview(
  classId: string,
  attemptId: string,
  draft: { score?: number | null; comment?: string | null }
): Promise<AttemptReview> {
  return api.put<AttemptReview>(`/classes/${classId}/attempts/${attemptId}/review`, draft);
}

/** Release the grade to the student; releasing again corrects it. */
export function releaseAttemptGrade(
  classId: string,
  attemptId: string,
  grade: { score: number; comment: string | null }
): Promise<AttemptReview> {
  return api.post<AttemptReview>(`/classes/${classId}/attempts/${attemptId}/release`, grade);
}

export function fetchMyGrades(offset = 0): Promise<Page<MyGrade>> {
  return api.get<Page<MyGrade>>(`/me/grades?limit=${GRADING_PAGE_SIZE}&offset=${offset}`);
}

// ── School admin: content approval ─────────────────────────────────────────

export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

/** A published bundle the school can use, with the school's decision on it. */
export interface ContentCatalogEntry {
  bundle_id: string;
  name: string;
  description?: string | null;
  /** The platform's content rather than the school's own. */
  platform_wide: boolean;
  item_count: number;
  approval_status: ApprovalStatus;
  tags: ContentTags;
  notes?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
}

export interface ContentApprovalInput extends ContentTags {
  approval_status: ApprovalStatus;
  notes?: string | null;
}

export function fetchContentCatalog(
  params: { approvalStatus?: ApprovalStatus | ''; search?: string },
  organizationId?: string | null
): Promise<ContentCatalogEntry[]> {
  const query = new URLSearchParams();
  if (organizationId) query.set('organization_id', organizationId);
  if (params.approvalStatus) query.set('approval_status', params.approvalStatus);
  if (params.search) query.set('search', params.search);
  const qs = query.toString();
  return api.get<ContentCatalogEntry[]>(`/student-system/content${qs ? `?${qs}` : ''}`);
}

export function setContentApproval(
  bundleId: string,
  input: ContentApprovalInput,
  organizationId?: string | null
): Promise<ContentCatalogEntry> {
  return api.put<ContentCatalogEntry>(`/student-system/content/${bundleId}${orgQuery(organizationId)}`, input);
}

// ── Sharing a class (access grants) ────────────────────────────────────────

/** `view` reads the roster, sessions, attempts and reports; `grade` can also grade. */
export type GrantScope = 'view' | 'grade';

export interface AccessGrant {
  grantee_id: string;
  display_name?: string | null;
  email?: string | null;
  scope: GrantScope;
  expires_at?: string | null;
  granted_by?: string | null;
  created_at: string;
  active: boolean;
}

/** The server refuses an expiry further out than this. */
export const GRANT_MAX_DAYS = 400;

export function fetchAccessGrants(classId: string, includeExpired = false): Promise<AccessGrant[]> {
  return api.get<AccessGrant[]>(`/classes/${classId}/access-grants${includeExpired ? '?include_expired=true' : ''}`);
}

/** Sharing again with the same person replaces their scope and expiry. */
export function shareClass(
  classId: string,
  grant: { email: string; scope: GrantScope; expires_at: string }
): Promise<AccessGrant> {
  return api.put<AccessGrant>(`/classes/${classId}/access-grants`, grant);
}

export function unshareClass(classId: string, granteeId: string): Promise<void> {
  return api.delete<void>(`/classes/${classId}/access-grants/${encodeURIComponent(granteeId)}`);
}

// ── School admin: consent status ───────────────────────────────────────────

export interface ConsentStatusEntry {
  student_id: string;
  display_name?: string | null;
  email?: string | null;
  /** Names of the active classes the student is in. */
  classes: string[];
  has_consent: boolean;
  consent_type?: ConsentType | null;
  granted_at?: string | null;
  granted_by?: string | null;
  guardian_id?: string | null;
}

export interface ConsentStatusPage {
  consent_required: boolean;
  consent_type: ConsentType;
  /** Counts are for every student, whatever the `consent` filter. */
  total: number;
  with_consent: number;
  missing_consent: number;
  items: ConsentStatusEntry[];
}

export function fetchConsentStatus(
  consent: 'missing' | 'granted' | '',
  organizationId?: string | null
): Promise<ConsentStatusPage> {
  const query = new URLSearchParams();
  if (organizationId) query.set('organization_id', organizationId);
  if (consent) query.set('consent', consent);
  const qs = query.toString();
  return api.get<ConsentStatusPage>(`/student-system/consent${qs ? `?${qs}` : ''}`);
}

// ── School admin: safety report queue ──────────────────────────────────────

export interface SafetyReport extends ContentReport {
  class_name?: string | null;
  student_id?: string | null;
  resolved_by?: string | null;
  resolved_at?: string | null;
  /** Open, on an archived class past its retention period: the class's data is kept
   *  until this report is resolved or dismissed. */
  holds_class_deletion: boolean;
}

export const SAFETY_PAGE_SIZE = 50;

export function fetchSafetyQueue(
  params: { status: ReportStatus | 'all'; page: number },
  organizationId?: string | null
): Promise<Page<SafetyReport>> {
  const query = new URLSearchParams({ status: params.status, page: String(params.page), page_size: String(SAFETY_PAGE_SIZE) });
  if (organizationId) query.set('organization_id', organizationId);
  return api.get<Page<SafetyReport>>(`/student-system/reports?${query.toString()}`);
}

export function reviewSafetyReport(
  reportId: string,
  status: ReportStatus,
  organizationId?: string | null
): Promise<SafetyReport> {
  return api.patch<SafetyReport>(`/student-system/reports/${reportId}${orgQuery(organizationId)}`, { status });
}

// ── Reports and exports ────────────────────────────────────────────────────

/** Assignments handed out (one per student per assignment) by where the student is. */
export interface ProgressCounts {
  completed: number;
  in_progress: number;
  not_started: number;
}

/** `average_score` is over released grades only, each as a percentage of its rubric's scale. */
interface Graded {
  graded_attempts: number;
  average_score?: number | null;
}

export interface ClassReportStudent extends ProgressCounts, Graded {
  student_id: string;
  display_name?: string | null;
  email?: string | null;
}

export interface ClassReportAssignment extends ProgressCounts, Graded {
  assignment_id: string;
  title: string;
  due_date?: string | null;
}

export interface ClassReport extends ProgressCounts, Graded {
  class_id: string;
  class_name: string;
  school_year?: string | null;
  status: ClassStatus;
  students: number;
  assignments: number;
  by_student: ClassReportStudent[];
  by_assignment: ClassReportAssignment[];
}

export interface SchoolReportClass extends ProgressCounts, Graded {
  class_id: string;
  class_name: string;
  school_year?: string | null;
  status: ClassStatus;
  students: number;
  assignments: number;
}

export interface SchoolReport extends ProgressCounts, Graded {
  organization_id: string;
  classes: number;
  students: number;
  by_class: SchoolReportClass[];
}

export type ExportFormat = 'csv' | 'xlsx';

export function fetchClassReport(classId: string): Promise<ClassReport> {
  return api.get<ClassReport>(`/classes/${classId}/report`);
}

export function fetchSchoolReport(
  params: { status?: ClassStatus | ''; schoolYear?: string },
  organizationId?: string | null
): Promise<SchoolReport> {
  return api.get<SchoolReport>(`/student-system/report?${schoolReportQuery(params, organizationId).toString()}`);
}

function schoolReportQuery(
  params: { status?: ClassStatus | ''; schoolYear?: string },
  organizationId?: string | null
): URLSearchParams {
  const query = new URLSearchParams();
  if (organizationId) query.set('organization_id', organizationId);
  if (params.status) query.set('status', params.status);
  if (params.schoolYear) query.set('school_year', params.schoolYear);
  return query;
}

/**
 * Fetch a report file with the auth header and hand it to the browser: a plain link to
 * the API would arrive unauthenticated, and `api` parses every response as JSON.
 */
async function download(path: string, filename: string): Promise<void> {
  const token = await getIdToken();
  const response = await fetch(getBackendUrl(path), { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data.detail || i18n.t('common.apiError', { status: response.status }));
    (error as any).status = response.status;
    throw error;
  }
  const url = window.URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export function downloadClassReport(classId: string, format: ExportFormat, filename: string): Promise<void> {
  return download(`/classes/${classId}/report/export?format=${format}`, `${filename}.${format}`);
}

export function downloadSchoolReport(
  params: { status?: ClassStatus | ''; schoolYear?: string },
  format: ExportFormat,
  filename: string,
  organizationId?: string | null
): Promise<void> {
  const query = schoolReportQuery(params, organizationId);
  query.set('format', format);
  return download(`/student-system/report/export?${query.toString()}`, `${filename}.${format}`);
}

// ── School admin: year-end rollover ────────────────────────────────────────

export interface RolloverClassPlan {
  class_id: string;
  /** The new class's name; the old one's when left out. */
  name?: string | null;
  /** The students who move on; every current student when left out. */
  student_ids?: string[] | null;
  include_teachers: boolean;
}

export interface RolloverResult {
  old_class_id: string;
  new_class_id: string;
  name: string;
  teachers: number;
  students: number;
}

/** All or nothing: one refused class leaves every class as it was. */
export function rollOverClasses(
  toSchoolYear: string,
  classes: RolloverClassPlan[],
  organizationId?: string | null
): Promise<{ classes: RolloverResult[] }> {
  return api.post<{ classes: RolloverResult[] }>(`/student-system/rollover${orgQuery(organizationId)}`, {
    to_school_year: toSchoolYear,
    classes,
  });
}
